<template>
  <div class="order-timeline">
    <div v-for="(value, key) in orderTimeline" :key="key">
      <v-slide-x-transition group>
        <div
          v-for="(step, index) in value"
          :key="index"
          class="order-timeline__item"
          :class="{
            failed: !step.icon,
            returned: step.icon === 'Returned',
            eta: step.time_type === 'eta'
          }"
        >
          <div class="item__desc" :class="{ check: step.icon === 'Check' }">
            <div class="item-icon">
              <SvgIcon
                v-if="step.icon"
                :height="step.iconSize || '12'"
                :width="step.iconSize || '12'"
                :icon="step.icon"
              />
            </div>
            <span>{{ step.text }}</span>
          </div>
          <div class="item__time">
            <span v-if="step.time_type !== 'eta' && index === 0">{{ key }}, </span>

            <span v-if="step.time_type === 'eta'">{{ step.eta }}</span>
            <span v-else>{{ step.time_formatted }}</span>
          </div>
        </div>
      </v-slide-x-transition>
    </div>
    <div class="order__por-info" v-if="porInfo" @click="handleClickPorInfo">
      <SvgIcon class="pictures-icon" width="16" height="16" icon="Pictures" />
      <span>{{
        this.order.returning_docs_status_label
          ? $t('record.view_returned_por_document')
          : $t('record.view_returned_por_info')
      }}</span>
    </div>

    <ViewImageModal v-if="porInfo" ref="viewImageModal" :img-src="porInfo" />
  </div>
</template>

<script>
import ViewImageModal from '@/components/ViewImageModal'
import { groupBy, sortBy } from '@/plugins/lodash'

export default {
  name: 'OrderTimeline',
  props: {
    order: {
      type: Object,
      required: true
    },
    porInfo: {
      type: String,
      default: ''
    },
    eta: {
      type: Number
    }
  },
  components: {
    ViewImageModal
  },
  data() {
    return {
      orderTimeline: []
    }
  },
  methods: {
    getTimelineText(key) {
      return this.$t('timeline.' + key)
    },
    getOrderTimeline() {
      let milestones = []
      const confirmed = {
        time: this.order.create_time,
        text: this.getTimelineText('created'),
        icon: 'Check'
      }
      const accepted = {
        time: this.order.accept_time,
        text: this.getTimelineText('accept'),
        icon: 'Check'
      }
      const pickedUp = {
        time: this.order.pickup_time || (!this.order.sub_status && this.order.board_time),
        text: this.getTimelineText('pickup'),
        icon: 'Check'
      }
      const completed = {
        time: this.order.complete_time,
        text: this.getTimelineText('complete'),
        icon: 'Check'
      }
      const cancelled = {
        time: this.order.cancel_time,
        text: this.getTimelineText('cancel'),
        icon: 'Check'
      }
      const returned = {
        time: this.order.path[0].return_time || this.order.return_time,
        text: this.getTimelineText('return'),
        icon: 'Returned',
        iconSize: '16'
      }
      // Return Document: only apply when order status = 'COMPLETED' || not apply for each dropOffPoint
      const returnedDocument = {
        time: this.order.path[0].return_time || this.order.return_time,
        text: this.getTimelineText('returned_document'),
        icon: 'Returned',
        iconSize: '16'
      }

      let pathTimeline = []
      const dropOffPoints = this.order.path.slice(1)
      const failMessage = dropOffPoints.length > 1 ? 'timeline.fail_package' : 'timeline.fail'
      const deliveryMessage =
        dropOffPoints.length > 1 ? 'timeline.delivery_package' : 'timeline.delivery'
      dropOffPoints.forEach((point, index) => {
        if (point.status === this.$constants.ORDER_STATUS.COMPLETED) {
          pathTimeline.push({
            time: point.complete_time,
            text: this.$t(deliveryMessage, { package_num: index + 1 }),
            icon: 'Check'
          })
        } else {
          pathTimeline.push({
            time: point.fail_time,
            text: this.$t(failMessage, { package_num: index + 1 }),
            icon: ''
          })
        }
      })

      // use nearest time to group eta status to same day
      // Allow to show estimated time of arrival
      const allowEta = this.$constants.SERVICES_ALLOW_ETA.some((s) => {
        return this.order.service_id.includes(s)
      })
      // eta status: picking up
      let pickingTimeFake = this.order.accept_time || this.$moment()
      pickingTimeFake = this.$moment.unix(pickingTimeFake).endOf('day').format('X')

      const pickingUp = {
        time_type: 'eta',
        time: pickingTimeFake,
        eta: (allowEta && this.eta && this.$t('record.eta_time', { t: this.eta })) || '',
        text: this.getTimelineText('picking'),
        icon: 'Dot',
        iconSize: '8'
      }
      // eta status: in process
      let inProcessTimeFake = pickedUp.time || this.$moment()
      inProcessTimeFake = this.$moment.unix(inProcessTimeFake).endOf('day').format('X')

      const inProcess = {
        time_type: 'eta',
        time: inProcessTimeFake,
        eta:
          (allowEta &&
            this.order.path.length === 2 &&
            this.eta &&
            this.$t('record.eta_time', { t: this.eta })) ||
          '',
        text: this.getTimelineText('in_process'),
        icon: 'Dot',
        iconSize: '8'
      }

      // eta status: returning
      let returningTimeFake = this.order.accept_time || this.$moment()
      returningTimeFake = this.$moment.unix(returningTimeFake).endOf('day').format('X')

      const returning = {
        time_type: 'eta',
        time: returningTimeFake,
        eta: '',
        text: this.getTimelineText('returning'),
        icon: 'Returning',
        iconSize: '16'
      }
      const returningDocument = {
        time_type: 'eta',
        time: returningTimeFake,
        text: this.getTimelineText('returning_document'),
        icon: 'Returning',
        iconSize: '16'
      }

      // Filter existsed status
      milestones = [confirmed, accepted, cancelled, pickedUp, ...pathTimeline]

      // Add status with conditions
      // Completed status
      if (this.order.path.length > 2) {
        milestones.push(completed)
      }

      // Returned/returning status
      if (this.order.returning_status_label) {
        if (this.order.returning_status_label.returned) {
          milestones.push(returned)
        } else {
          milestones.push(returning)
        }
      }

      // Picking up status
      if (this.order.status === this.$constants.ORDER_STATUS.ACCEPTED && !pickedUp.time) {
        milestones.push(pickingUp)
      }

      // InProcess status
      if (this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS && !completed.time) {
        milestones.push(inProcess)
      }

      // Return Docs status
      if (this.order.returning_docs_status_label) {
        if (this.order.returning_docs_status_label.returned) {
          milestones.push(returnedDocument)
        } else {
          milestones.push(returningDocument)
        }
      }

      milestones = sortBy(milestones, 'time')
      let timeline = milestones
        .filter((item) => item.time)
        .map((item) => {
          if (item.time_formatted) {
            return item
          } else {
            return {
              ...item,
              time_formatted: this.$moment.unix(item.time).format('h:mm A')
            }
          }
        })

      let timelineGroup = groupBy(timeline, (item) => {
        return this.formatTime(item.time)
      })
      // console.log('timelineGroup: ', timelineGroup)
      return timelineGroup // timeline
    },
    formatTime(time) {
      if (this.$moment.unix(time).isSame(this.$moment(), 'day')) return this.$t('record.days.today')
      // console.log('this.$moment.unix(time).', this.$moment.unix(time).format('DD/MM'))
      return this.$moment.unix(time).format('DD/MM')
    },
    handleClickPorInfo() {
      this.$refs.viewImageModal.showModal = true
    }
  },
  mounted() {
    this.orderTimeline = this.getOrderTimeline()
  },
  watch: {
    order() {
      this.orderTimeline = this.getOrderTimeline()
    },
    eta(val, oldVal) {
      if (
        val !== oldVal &&
        (this.order.status === this.$constants.ORDER_STATUS.ACCEPTED ||
          this.order.status === this.$constants.ORDER_STATUS.IN_PROCESS)
      ) {
        this.orderTimeline = this.getOrderTimeline()
      }
    }
  }
}
</script>

<i18n src="@/locales2/share/orderTimeline.json"></i18n>

<style lang="scss">
.order-timeline {
  .order__por-info {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
    background-color: #f2f2f2;
    border-radius: 4px;
    margin-left: 28px;
    cursor: pointer;

    .pictures-icon {
      svg {
        path {
          fill: #7a8189;
        }
      }
    }

    > span {
      margin-left: 6px;
    }
  }

  .order-timeline__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    color: #1a1a1a;

    &.failed {
      color: #1a1a1a !important;
      font-weight: 500;
    }

    &.returned {
      color: #1dc05e !important;
      font-weight: 500;
    }

    &.eta {
      color: #ff8200 !important;
      font-weight: 500;
    }

    .item__desc {
      display: flex;
      align-items: center;
      margin-right: 4px;

      &.check {
        align-items: baseline;
        color: #7a8189;
      }

      .item-icon {
        margin-right: 8px;
        width: 20px;
        height: 20px;
      }
    }

    .item__time {
      margin-left: 4px;
    }
  }
}
</style>
